import {
  calculateVelocitySimulation,
  clearVelocitySimulations,
  selectActiveProject,
  selectVelocityModelsByActiveProject,
  selectVelocitySimulationResultByProjectId,
  selectVelocitySimulationState,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { ResultCreateVelocityModel } from '@core/core/types'
import { useEffect, useState } from 'react'

import { layersMenuChoiceItems, modelMenuChoiceItems } from '../constants'
import type {
  AddedSelectItemType,
  ChoiceItemsType,
  ChosenVelocityModel,
  FormDataType,
  SimulateVelocityResults,
} from '../types'

export const useSimulateVelocity = (): SimulateVelocityResults => {
  const dispatch = useAppDispatch()
  const activeProject = useAppSelector(selectActiveProject)
  const activeResult = useAppSelector(state =>
    selectVelocitySimulationResultByProjectId(state, activeProject.id),
  )
  const existingModelsList = useAppSelector(selectVelocityModelsByActiveProject)
  const velocitySimulationState = useAppSelector(selectVelocitySimulationState)

  const velocitySimulationErrorMessage = velocitySimulationState.error?.message
  const isCalculating = velocitySimulationState.status === 'calculating'

  const [formData, setFormData] = useState<FormDataType>({
    model: {
      name: 'Модель интервальных скоростей глубин 1',
    },
    reductionLevel: { depth: 0, speed: 1480 },
    source: {
      mapId: null,
      markerId: null,
    },
  })

  const [chosenVelocityModel, setChosenVelocityModel] = useState<ChosenVelocityModel>(null)
  const [modelResults, setModelResults] = useState<ResultCreateVelocityModel>()
  const [addedSelectItemList, setAddedSelectItemList] = useState<AddedSelectItemType[]>([])
  const [choiceValue, setChoiceValue] = useState<ChoiceItemsType>({
    layers: layersMenuChoiceItems[0],
    model: modelMenuChoiceItems[0],
  })

  useEffect(() => {
    if (activeResult) {
      setModelResults(activeResult)
    }
  }, [activeResult])

  const onChangeVelocityModel = (value: ChosenVelocityModel) => {
    setChosenVelocityModel(value)
  }

  const isRecalculating = choiceValue.model !== modelMenuChoiceItems[0]

  const onCalculate = () => {
    dispatch(clearVelocitySimulations())
    dispatch(
      calculateVelocitySimulation({
        projectId: activeProject.id,
        modelId: isRecalculating ? chosenVelocityModel?.id : undefined,
        data: {
          name: (isRecalculating ? chosenVelocityModel?.name : formData.model.name) ?? '',
          borderVelocityTop: {
            depth: formData.reductionLevel.depth ?? 0,
            velocity: formData.reductionLevel.speed ?? 0,
          },
          layers: addedSelectItemList.map(item => ({
            key: item.id,
            gridId: item.map?.value?.entityId ?? '',
            markerName: item.marker?.value?.entityName ?? '',
          })),
          borderVelocityBot: {
            depth: formData.reductionLevel.depth ?? 0,
            velocity: formData.reductionLevel.speed ?? 0,
          },
        },
      }),
    )
  }

  const clearModalData = () => dispatch(clearVelocitySimulations())

  const activeResultStatus = activeResult?.status ?? 'Invalidated'

  const calculationStatus = {
    status: !velocitySimulationErrorMessage ? activeResultStatus : 'Failed',
    note: activeResult?.note ?? velocitySimulationErrorMessage,
  }

  const canCalculate =
    addedSelectItemList.length > 0 &&
    addedSelectItemList.every(item => item.map?.value && item.marker?.value) &&
    formData.reductionLevel.depth != null &&
    formData.reductionLevel.speed != null &&
    !!(isRecalculating ? chosenVelocityModel : formData.model.name.length)

  return {
    calculationStatus,
    modelResults,
    existingModelsList,
    chosenVelocityModel,
    onChangeVelocityModel,
    formData,
    onCalculate,
    setFormData,
    addedSelectItemList,
    setAddedSelectItemList,
    canCalculate,
    isCalculating,
    clearModalData,
    choiceValue,
    setChoiceValue,
  }
}
